import {
    formatSeriesData,
    getSeriesColors,
} from './Charts/MultiPredictionTimeSeriesChart/utils';
import { forwardRef, memo, useMemo } from 'react';
import Chart from './Charts/MultiPredictionTimeSeriesChart';
import { CombinedTimeSeriesOverview } from 'components/TimeSeriesOverview';
import { POPULARITY_METADATA_BY_KEY } from 'utils/metrics';
import { StatDisplay } from '../TimeSeriesOverview';
import TimeSeriesKey from './TimeSeriesKey';
import Tooltip from './Charts/MultiPredictionTimeSeriesChart/components/Tooltip';
import { calculateOverviewData } from 'utils/helpers';
import { timeseries as copy } from '@/copy';
import dayjs from 'dayjs';
import { getAverageTimeSeries } from './utils/index';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';

const defaultPopularityMetrics = [
    POPULARITY_METADATA_BY_KEY.popularity_index_avg_12_month,
    POPULARITY_METADATA_BY_KEY.YoY,
    POPULARITY_METADATA_BY_KEY.QoQ,
    POPULARITY_METADATA_BY_KEY.MoM,
];

const defaultPredictionMetrics = [POPULARITY_METADATA_BY_KEY.predicted_YoY];

const formatAndParseTime = (v) => {
    const s = dayjs(v, 'YYYY-MM-DD').format('MMM D, YYYY');
    return s;
};

const OverviewTooltip = forwardRef(
    (
        {
            formatAndParseTime: formatAndParseTimeProp = formatAndParseTime,
            metricLabel = 'Combined Pop.',
            ...props
        },
        ref,
    ) => (
        <Tooltip
            {...props}
            formatAndParseTime={formatAndParseTimeProp}
            metricLabel={metricLabel}
            ref={ref}
        />
    ),
);

const WatchlistOverviewTimeSeriesWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const WatchlistOverviewTimeSeries = memo(
    forwardRef(
        (
            {
                data,
                isLoading,
                noDataMessage,
                viewSumTimeSeries,
                Wrapper = WatchlistOverviewTimeSeriesWrapper,
                Tooltip = OverviewTooltip,
                ...props
            },
            ref,
        ) => {
            let maybeSumData = data;

            if (viewSumTimeSeries && data && data.length) {
                const { accessors } = formatSeriesData(data);
                maybeSumData = [
                    {
                        id: 'combined',
                        timeSeries: getAverageTimeSeries(data, accessors),
                        color: getSeriesColors(0),
                        name: 'Combined',
                    },
                ];
            }
            const overviewData = useMemo(
                () => (isLoading ? {} : calculateOverviewData(data) || {}),
                [data, isLoading],
            );
            const { popularity_index_avg_12_month, ...metrics } = overviewData;
            const combinedMetricsList = defaultPopularityMetrics.slice(1);
            return (
                <div
                    css={`
                        position: relative;
                    `}
                    ref={ref}
                >
                    <Chart
                        chartHeight={350}
                        data={maybeSumData}
                        isLoading={isLoading}
                        showPlaceholder={!data || data.length < 1}
                        placeholderMessage={
                            isLoading ? copy.loading : noDataMessage
                        }
                        Wrapper={Wrapper}
                        Tooltip={Tooltip}
                    >
                        <div
                            css={`
                                display: flex;
                                align-items: flex-start;
                                justify-content: space-between;
                            `}
                        >
                            <StatDisplay
                                popularity_index_avg_12_month={
                                    popularity_index_avg_12_month
                                }
                                isLoading={isLoading}
                                valueKey="popularity_index_avg_12_month"
                                tooltipKey="popularity"
                                contentKey="dataTablePopularityIndex"
                                labelPrefix="Avg. Monthly"
                                labelSuffix="Popularity"
                            />
                            <CombinedTimeSeriesOverview
                                isLoading={isLoading}
                                metrics={
                                    metrics
                                        ? [
                                              {
                                                  metrics,
                                                  metricsList:
                                                      combinedMetricsList,
                                                  defaultMetric:
                                                      combinedMetricsList[0],
                                              },
                                          ]
                                        : null
                                }
                                predictionMetrics={
                                    metrics
                                        ? [
                                              {
                                                  metrics,
                                                  metricsList:
                                                      defaultPredictionMetrics,
                                                  defaultMetric:
                                                      defaultPredictionMetrics[0],
                                              },
                                          ]
                                        : null
                                }
                                predictionMetricsCss="margin-left: auto;"
                                showDataSource={false}
                                showDataSourceBar={false}
                                customCss="display: block;"
                                wrapperCss="width: 74%; padding: 0 16px;"
                            />
                        </div>
                    </Chart>
                    {!viewSumTimeSeries ? (
                        <TimeSeriesKey
                            data={data}
                            style={{ display: 'none', marginTop: '1em' }}
                            className="image-download-visible"
                        />
                    ) : null}
                </div>
            );
        },
    ),
    isEqual,
);

export default WatchlistOverviewTimeSeries;
